// 路由历史管理工具
const ROUTE_HISTORY_KEY = 'route_history';

export interface RouteHistoryItem {
  path: string;
  prevPath?: string | null;
  search: string;
  hash: string;
  fullPath: string;
  timestamp: number;
  navigationType: 'PUSH' | 'REPLACE' | 'POP';
}

export class RouteHistoryManager {
  private static instance: RouteHistoryManager;
  private maxHistorySize: number = 50; // 最大历史记录数量

  private constructor() {}

  static getInstance(): RouteHistoryManager {
    if (!RouteHistoryManager.instance) {
      RouteHistoryManager.instance = new RouteHistoryManager();
    }
    return RouteHistoryManager.instance;
  }

  // 获取历史记录
  getHistory(): RouteHistoryItem[] {
    try {
      const history = sessionStorage.getItem(ROUTE_HISTORY_KEY);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('获取路由历史记录失败:', error);
      return [];
    }
  }

  // 添加路由记录
  addRoute(location: { pathname: string; search: string; hash: string }, navigationType: 'PUSH' | 'REPLACE' | 'POP') {
    try {
      const history = this.getHistory();
      const fullPath = `${location.pathname}${location.search}${location.hash}`;
      
      const newItem: RouteHistoryItem = {
        path: location.pathname,
        search: location.search,
        hash: location.hash,
        fullPath,
        timestamp: Date.now(),
        navigationType
      };

      // 根据导航类型处理历史记录
      if (navigationType === 'REPLACE') {
        // 替换最后一个记录
        if (history.length > 0) {
          history[history.length - 1] = newItem;
        } else {
          history.push(newItem);
        }
      } else if (navigationType === 'POP') {
        // 后退操作，不需要添加新记录，只记录当前状态
        // 可以选择更新最后一个记录或者不做处理
        if (history.length > 0) {
          history[history.length - 1] = newItem;
        }
      } else {
        // PUSH 操作，添加新记录
        history.push(newItem);
      }

      // 限制历史记录数量
      if (history.length > this.maxHistorySize) {
        history.splice(0, history.length - this.maxHistorySize);
      }

      sessionStorage.setItem(ROUTE_HISTORY_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('添加路由历史记录失败:', error);
    }
  }

  // 获取当前页面路径
  getCurrentPath(): string | null {
    const history = this.getHistory();
    return history.length > 0 ? history[history.length - 1].fullPath : null;
  }

  // 获取上一个页面路径
  getPreviousPath(): string | null {
    const history = this.getHistory();
    return history.length > 1 ? history[history.length - 2].fullPath : null;
  }

  // 获取路径列表（只返回路径字符串）
  getPathList(): string[] {
    return this.getHistory().map(item => item.fullPath);
  }

  // 获取最近的几个路径
  getRecentPaths(count: number = 10): RouteHistoryItem[] {
    const history = this.getHistory();
    return history.slice(-count);
  }

  // 清空历史记录
  clearHistory() {
    try {
      sessionStorage.removeItem(ROUTE_HISTORY_KEY);
    } catch (error) {
      console.error('清空路由历史记录失败:', error);
    }
  }

  // 设置最大历史记录数量
  setMaxHistorySize(size: number) {
    this.maxHistorySize = size;
  }

  // 根据路径过滤历史记录
  filterByPath(pathPattern: string): RouteHistoryItem[] {
    const history = this.getHistory();
    return history.filter(item => item.path.includes(pathPattern));
  }

  // 获取访问频率统计
  getPathFrequency(): Record<string, number> {
    const history = this.getHistory();
    const frequency: Record<string, number> = {};
    
    history.forEach(item => {
      frequency[item.path] = (frequency[item.path] || 0) + 1;
    });
    
    return frequency;
  }
}

// 导出单例实例
export const routeHistoryManager = RouteHistoryManager.getInstance();

// 便捷方法
export const getRouteHistory = () => routeHistoryManager.getHistory();
export const getRoutePathList = () => routeHistoryManager.getPathList();
export const getCurrentRoutePath = () => routeHistoryManager.getCurrentPath();
export const getPreviousRoutePath = () => routeHistoryManager.getPreviousPath();
export const clearRouteHistory = () => routeHistoryManager.clearHistory(); 